import { useState } from "react";
import { formatShortDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDots, Sparkle, TrendUp } from "@phosphor-icons/react";

const ownerStyles = {
  A: "border-[hsl(var(--input))] bg-[hsl(var(--background))] text-[hsl(var(--muted-foreground))]",
  B: "border-[hsl(var(--input))] bg-[hsl(var(--background))] text-[hsl(var(--muted-foreground))]",
  joint: "border-[hsl(var(--accent))] bg-[hsl(var(--background))] text-[#8a6d3f]",
};

const classificationStyles = {
  obligation: "bg-[#f5ecd9] text-[#9a6f2b]",
  opportunity: "bg-[#e7efe6] text-[#4f7a5b]",
};

const formatMoney = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

export default function CandidateTrayCard({
  candidate,
  ownerLabel,
  busy = false,
  onAccept,
  onDismiss,
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(candidate.title || "");
  const [owner, setOwner] = useState(candidate.ownerRaw || "");
  const [gateDate, setGateDate] = useState(candidate.gateDate || "");

  const isOpportunity = candidate.classification === "opportunity";
  const tone = classificationStyles[candidate.classification] || classificationStyles.obligation;
  const Icon = isOpportunity ? TrendUp : Sparkle;

  const submitAccept = () => {
    const overrides = {};
    if (title !== candidate.title) overrides.title = title;
    if (owner !== candidate.ownerRaw) overrides.owner = owner;
    if (gateDate !== (candidate.gateDate || "")) overrides.gate_date = gateDate;
    onAccept(candidate, overrides);
  };

  return (
    <article
      data-testid="candidate-card"
      className={`rounded-[1rem] border px-4 py-4 ${
        isOpportunity ? "border-[#bfd6c0] bg-[#f7fbf8]" : "border-[hsl(var(--border))] bg-card"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] ${tone}`}>
          <Icon size={12} weight="fill" />
          {isOpportunity ? "Opportunity candidate" : "Feeder candidate"}
        </span>
        <span
          className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] ${
            ownerStyles[candidate.ownerParty] || ownerStyles.joint
          }`}
        >
          {candidate.ownerLabel || ownerLabel(candidate.ownerParty)}
        </span>
      </div>

      {editing ? (
        <div className="mt-3 space-y-3">
          <Input
            data-testid="candidate-edit-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="h-9 rounded-[0.625rem] border-[hsl(var(--input))] bg-white text-sm"
            placeholder="Title"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
              className="h-9 rounded-[0.625rem] border-[hsl(var(--input))] bg-white text-sm"
              placeholder="Owner"
            />
            <Input
              type="date"
              value={gateDate}
              onChange={(event) => setGateDate(event.target.value)}
              className="h-9 rounded-[0.625rem] border-[hsl(var(--input))] bg-white text-sm"
            />
          </div>
        </div>
      ) : (
        <>
          <h3 className="mt-3 font-serif text-[0.95rem] text-[hsl(var(--foreground))]">{candidate.title}</h3>
          <p className="mt-2 text-[12.5px] text-[hsl(var(--muted-foreground))]">
            {[
              candidate.counterparty,
              candidate.gateDate ? `Gate ${formatShortDate(candidate.gateDate)}` : null,
              isOpportunity && candidate.decayDate ? `Decays ${formatShortDate(candidate.decayDate)}` : null,
            ]
              .filter(Boolean)
              .join(" · ") || "Awaiting operator review"}
          </p>
        </>
      )}

      {!editing && candidate.amount !== null ? (
        <p className="mt-2 font-mono tabular-nums text-sm text-[hsl(var(--foreground))]">{formatMoney(candidate.amount)}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button
              type="button"
              className="rounded-[0.625rem]"
              disabled={busy}
              onClick={submitAccept}
              data-testid="candidate-accept"
            >
              {busy ? "Accepting…" : "Confirm accept"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-[0.625rem] border-[hsl(var(--input))]"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setTitle(candidate.title || "");
                setOwner(candidate.ownerRaw || "");
                setGateDate(candidate.gateDate || "");
              }}
            >
              Cancel edit
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              className="rounded-[0.625rem]"
              disabled={busy}
              onClick={() => setEditing(true)}
              data-testid="candidate-accept"
            >
              Accept
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-[0.625rem] border-[hsl(var(--input))]"
              disabled={busy}
              onClick={() => onDismiss(candidate)}
              data-testid="candidate-dismiss"
            >
              Dismiss
            </Button>
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 border-t border-[hsl(var(--border))] pt-3 text-[11.5px] text-[hsl(var(--muted-foreground))]">
        {isOpportunity ? <TrendUp size={13} className="text-[#4f7a5b]" /> : <Sparkle size={13} className="text-[hsl(var(--accent))]" />}
        <span className="truncate">{candidate.sourceLine}</span>
      </div>

      {isOpportunity && candidate.decayDate ? (
        <div className="mt-2 flex items-center gap-2 text-[11.5px] text-[#4f7a5b]">
          <CalendarDots size={13} />
          <span>Decay date {formatShortDate(candidate.decayDate)}</span>
        </div>
      ) : null}
    </article>
  );
}
